import React from "react";
import Link from "next/link";
import Image from "next/image";
import { useDispatch } from "react-redux";
import { setIsAuthenticationOpen } from "lib/redux/uiSlice";
// import CustomIcon from "components/CustomIcon";
// import ExperienceSelector from './ExperienceSelector';
import { useAppSelector } from "lib/redux/hooks";
import {
  selectAnalysis,
  selectExperienceLevel,
} from "lib/redux/scoreAnalysisSlice";
// import { selectResume } from "lib/redux/resumeSlice";
import { ExperienceSwitch } from "./ExperienceSwitch";
import { ResumeAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";
// import { Resume } from "lib/redux/types";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  accessToken: string | null;
  handleLogout: () => void;
  handleSaveResume: () => void;
  handleResumeShare: () => void;
  calculateAndUpdateScore?: (
    analysisData: ResumeAnalysis,
    expLevel: "FRESHER" | "EXPERIENCED"
    // , currentResume: Resume
  ) => void;
  handleAnalyseResume: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  accessToken,
  handleLogout,
  handleSaveResume,
  handleResumeShare,
  handleAnalyseResume,
  calculateAndUpdateScore,
}) => {
  const analysis = useAppSelector(selectAnalysis);
  const experienceLevel = useAppSelector(selectExperienceLevel);
  // const resume = useAppSelector(selectResume);
  const dispatch = useDispatch();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Menu panel */}
      <div className="absolute right-0 top-0 h-full w-64 transform bg-white p-5 shadow-xl transition-transform duration-300 ease-in-out">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Menu</h2>
          <button onClick={onClose} className="p-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="flex flex-col space-y-4">
          {/* Recheck score button */}
          <button
            onClick={() => {
              handleAnalyseResume();
              onClose();
            }}
            className="flex items-center gap-2 rounded-lg bg-pink-50 px-4 py-3 font-medium text-purple-600"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.9974 13.3333C11.3807 13.3333 12.4974 12.2167 12.4974 10.8333C12.4974 9.9 11.9891 9.08333 11.2474 8.65833L4.77397 4.91161C4.331 4.65523 3.8346 5.15106 4.09047 5.59432L7.76406 11.9583C8.18073 12.775 9.0224 13.3333 9.9974 13.3333ZM9.9974 2.5C8.67821 2.5 7.43551 2.81872 6.32506 3.35581C6.11528 3.45727 6.11229 3.74783 6.3142 3.86417L7.60573 4.60833C8.33073 4.325 9.16406 4.16667 9.9974 4.16667C13.6807 4.16667 16.6641 7.15 16.6641 10.8333C16.6641 12.675 15.9224 14.3417 14.7141 15.5417H14.7057C14.6285 15.6188 14.5672 15.7103 14.5254 15.8111C14.4836 15.912 14.462 16.02 14.462 16.1292C14.462 16.2383 14.4836 16.3464 14.5254 16.4472C14.5672 16.548 14.6285 16.6396 14.7057 16.7167C15.0307 17.0417 15.5641 17.0417 15.8891 16.725C16.664 15.9522 17.2787 15.0339 17.6977 14.0228C18.1167 13.0117 18.3319 11.9278 18.3307 10.8333C18.3307 6.25 14.5807 2.5 9.9974 2.5ZM1.66406 10.8333C1.66406 13.1333 2.5974 15.2167 4.10573 16.725C4.43073 17.0417 4.95573 17.0417 5.28073 16.7167C5.35798 16.6396 5.41927 16.548 5.46109 16.4472C5.50291 16.3464 5.52443 16.2383 5.52443 16.1292C5.52443 16.02 5.50291 15.912 5.46109 15.8111C5.41927 15.7103 5.35798 15.6188 5.28073 15.5417C4.66098 14.9244 4.16958 14.1906 3.83489 13.3825C3.50021 12.5744 3.32886 11.708 3.33073 10.8333C3.33073 10 3.48906 9.16667 3.78073 8.41667L3.03762 7.12697C2.92105 6.92466 2.62966 6.9281 2.52813 7.13836C1.98383 8.26554 1.66406 9.50467 1.66406 10.8333Z"
                fill="currentColor"
              />
            </svg>

            <span>Recheck score</span>
          </button>

          {/* Save button */}
          <button
            onClick={() => {
              handleSaveResume();
              onClose();
            }}
            className="flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-3 font-medium text-blue-600"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            <span>Save resume</span>
          </button>

          {/* Share button */}
          <button
            onClick={() => {
              handleResumeShare();
              onClose();
            }}
            className="flex items-center gap-2 rounded-lg bg-blue-50 px-4 py-3 font-medium text-blue-600"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
            <span>Share resume</span>
          </button>
          <ExperienceSwitch
            selectedExperience={experienceLevel}
            onChange={(expLevel) => {
              if (calculateAndUpdateScore && analysis ) {
                calculateAndUpdateScore(analysis, expLevel);
              }
            }}
            className="mt-4"
          />

          <hr className="border-gray-200" />

          {/* Navigation links */}
          <Link
            href="/"
            className="flex items-center gap-x-2 py-3 transition-colors hover:text-blue-500"
          >
            <Image
              className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
              src="/icons/home.svg"
              alt="home icon"
              width={30}
              height={30}
            />
            <span>Home</span>
          </Link>

          <Link
            href="/resume-builder"
            className="flex items-center gap-x-2 py-3 transition-colors hover:text-blue-500"
          >
            <Image
              className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
              src="/icons/editWhite.svg"
              alt="builder icon"
              width={30}
              height={30}
            />
            <span>Edit in builder</span>
          </Link>

          {accessToken && (
            <Link
              href="/dashboard"
              className="flex items-center gap-x-2 py-3 transition-colors hover:text-blue-500"
            >
              <Image
                className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
                src="/assets/dashboardIcon.svg"
                alt="dashboard icon"
                width={30}
                height={30}
              />
              <span>Dashboard</span>
            </Link>
          )}

          {accessToken ? (
            <button
              onClick={handleLogout}
              className="flex items-center gap-x-2 py-3 transition-colors hover:text-blue-500"
            >
              <Image
                className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
                src="/icons/logout.svg"
                alt="logout icon"
                width={30}
                height={30}
              />
              <span>Sign out</span>
            </button>
          ) : (
            <button
              onClick={() => dispatch(setIsAuthenticationOpen(true))}
              className="flex items-center gap-x-2 py-3 transition-colors hover:text-blue-500"
            >
              <Image
                className="rounded-full bg-gradient-to-r from-[#88c4fc] to-[#1b91fe] p-1"
                src="/icons/logout.svg"
                alt="login icon"
                width={30}
                height={30}
              />
              <span>Sign in</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
